import i18n from 'i18next';
import {Button, IconButton} from 'platform/components';
import {Clickable, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import type {BulkOperationSelection} from '../../types/BulkOperationActionCallback';
import type {BulkOperationPresentation} from '../../types/BulkOperationPresentation';
import {getAvailableBulkOperationTargets} from '../../utils/getAvailableBulkOperationTargets';
import {getBulkOperationSelectionLabel} from './utils/getBulkOperationSelectionLabel';

type BulkOperationSelectionSummaryProps = {
  selection: BulkOperationSelection;
  resourceId: string | undefined;
  presentation?: BulkOperationPresentation;
  isSelectionActive: boolean;
  hasVisibleOperation: boolean;
  pageRecordIds?: string[];
  onSelectionChange?: (recordIds: string[]) => void;
  onFilteredSelectionChange?: (selected: boolean) => void;
};

export function BulkOperationSelectionSummary(props: BulkOperationSelectionSummaryProps) {
  const formatNumber = useFormatNumber();
  const selection = props.selection;
  const targets = getAvailableBulkOperationTargets(selection);

  const selectionLabel = getBulkOperationSelectionLabel({
    selection,
    resourceId: props.resourceId,
    presentation: props.presentation,
    formatNumber,
  });

  return (
    <>
      <Show when={props.isSelectionActive}>
        <Show when={props.onSelectionChange !== undefined}>
          <IconButton
            icon="content/remove"
            priority="primary"
            size="small"
            aria-label={i18n.t('general.actions.clear')}
            onClick={() => props.onSelectionChange?.([])}
          />
        </Show>
        <VStack spacing={0} align="flex-start">
          <Text size="small">
            <strong>{selectionLabel}</strong>
          </Text>
          <HStack spacing={2} align="center" wrap>
            <Show
              when={
                !selection.allFilteredSelected &&
                targets.filtered !== null &&
                props.onFilteredSelectionChange !== undefined &&
                (props.pageRecordIds?.length ?? 0) > 0 &&
                props.pageRecordIds?.every((id) => selection.recordIds.includes(id))
              }
            >
              <Clickable
                role="button"
                tabIndex={0}
                onClick={() => props.onFilteredSelectionChange?.(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    props.onFilteredSelectionChange?.(true);
                  }
                }}
              >
                <Text color="link" size="xSmall">
                  <strong>
                    {i18n.t('general.actions.selectAll')}{' '}
                    {formatNumber(selection.query.totalCount ?? 0, 0)}{' '}
                    {i18n
                      .t('page.datagrid.labels.filtered')
                      .toLocaleLowerCase(i18n.resolvedLanguage)}
                  </strong>
                </Text>
              </Clickable>
            </Show>
            <Show when={selection.allFilteredSelected}>
              <Button
                title={`${i18n.t('general.actions.select')} · ${i18n.t('general.labels.page')}`}
                variant="link"
                size="small"
                onClick={() => props.onFilteredSelectionChange?.(false)}
              />
            </Show>
            <Show when={props.onSelectionChange !== undefined}>
              <Button
                title={i18n.t('general.actions.clear')}
                variant="link"
                size="small"
                onClick={() => props.onSelectionChange?.([])}
              />
            </Show>
          </HStack>
        </VStack>
      </Show>
      <Show
        when={
          selection.recordIds.length === 0 &&
          !selection.allFilteredSelected &&
          !props.hasVisibleOperation
        }
      >
        <Text size="small" alternative>
          {i18n.t('general.notifications.noItemsSelected')} ·{' '}
          {(props.resourceId === undefined
            ? undefined
            : props.presentation?.getApplyToAllLabel?.(props.resourceId)) ??
            i18n.t('general.actions.apply')}{' '}
          · {i18n.t('page.datagrid.labels.filtered')}:{' '}
          {formatNumber(selection.query.totalCount ?? 0, 0)}
        </Text>
      </Show>
    </>
  );
}
